import { TRPCClientError } from '@trpc/client';
import { createTRPCClient, httpBatchLink, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';

import type { TRPCRouter } from '@backend/trpc/trpcRouter';

import { QUERY_CLIENT } from '@frontend/const/const.QUERY_CLIENT';
import { acquireToken } from '@frontend/features/auth/util/util.auth.acquireToken';
import { getAuthMode } from '@frontend/features/auth/util/util.auth.getAuthMode';
import { clearLocalToken } from '@frontend/features/auth/util/util.auth.localToken';

/**
 * Flag to prevent multiple simultaneous reloads
 */
let isReloadingForAuth = false;

/**
 * Circuit breaker for authentication failures
 * Prevents infinite retry loops when auth repeatedly fails
 */
let consecutiveAuthFailures = 0;
const MAX_CONSECUTIVE_AUTH_FAILURES = 3;
const CIRCUIT_BREAKER_RESET_MS = 30000; // 30 seconds

let circuitBreakerTimer: NodeJS.Timeout | null = null;

function resetCircuitBreaker() {
  consecutiveAuthFailures = 0;
  if (circuitBreakerTimer) {
    clearTimeout(circuitBreakerTimer);
    circuitBreakerTimer = null;
  }
}

function incrementAuthFailure() {
  consecutiveAuthFailures++;

  // Reset the circuit breaker timer
  if (circuitBreakerTimer) {
    clearTimeout(circuitBreakerTimer);
  }

  // Auto-reset after timeout
  circuitBreakerTimer = setTimeout(() => {
    console.log(
      '[AUTH] Circuit breaker reset - allowing authentication attempts',
    );
    resetCircuitBreaker();
  }, CIRCUIT_BREAKER_RESET_MS);
}

/**
 * Custom error handling link for localhost 401 responses
 * Clears expired tokens and reloads the page to show login
 */
const localhostErrorHandlerLink: TRPCLink<TRPCRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          observer.next(value);
        },
        error(err) {
          const authMode = getAuthMode();

          // Debug logging - log full error structure
          console.log('[AUTH DEBUG] Error caught:', {
            authMode,
            isTRPCClientError: err instanceof TRPCClientError,
            errorMessage: err instanceof Error ? err.message : 'unknown',
            httpStatus:
              err instanceof TRPCClientError ? err.data?.httpStatus : 'N/A',
            errorData: err instanceof TRPCClientError ? err.data : 'N/A',
            errorShape: err instanceof TRPCClientError ? err.shape : 'N/A',
            // Check if there's meta information with status
            errorMeta: err instanceof TRPCClientError ? err.meta : 'N/A',
            // Log the full error for debugging
            fullError: err,
          });

          // Handle 401 errors in E2E mode
          if (authMode === 'e2e' && err instanceof TRPCClientError) {
            // Check multiple locations for 401 status
            const is401Error =
              err.data?.httpStatus === 401 ||
              err.shape?.data?.httpStatus === 401 ||
              ('meta' in err &&
                typeof err.meta === 'object' &&
                err.meta !== null &&
                'response' in err.meta &&
                typeof err.meta.response === 'object' &&
                err.meta.response !== null &&
                'status' in err.meta.response &&
                err.meta.response.status === 401) ||
              err.message.includes('Unauthorized') ||
              err.message.includes('No authentication token provided') ||
              err.message.includes('Unable to transform response from server');

            if (is401Error) {
              // Prevent multiple simultaneous reloads
              if (isReloadingForAuth) {
                console.log(
                  '[AUTH] Already reloading, suppressing additional 401',
                );
                // Silently suppress this error since we're already reloading
                return;
              }

              console.log(
                '[AUTH] 401 detected in E2E mode - clearing token and reloading',
              );

              // Set flag to prevent concurrent reloads
              isReloadingForAuth = true;

              // Clear the expired token from localStorage
              clearLocalToken();

              // Reload the page to trigger AuthGuard → LoginPage → TokenExchangePage
              window.location.reload();

              // Don't propagate the error since we're reloading
              return;
            }
          }

          // Pass through all other errors
          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });

      return unsubscribe;
    });
  };
};

const trpcClient = createTRPCClient<TRPCRouter>({
  links: [
    // Error handler must come before httpBatchLink to intercept responses
    localhostErrorHandlerLink,
    httpBatchLink({
      url: '/v1',
      async headers() {
        try {
          // Circuit breaker: Stop attempting auth if too many failures
          if (consecutiveAuthFailures >= MAX_CONSECUTIVE_AUTH_FAILURES) {
            console.error(
              `[AUTH] Circuit breaker open - ${consecutiveAuthFailures} consecutive failures. Waiting ${CIRCUIT_BREAKER_RESET_MS / 1000}s before retry.`,
            );
            throw new Error(
              'Authentication temporarily unavailable. Please wait and try again.',
            );
          }

          const token = await acquireToken();

          // Success - reset circuit breaker
          resetCircuitBreaker();

          return {
            Authorization: `Bearer ${token}`,
          };
        } catch (authError) {
          console.log('[AUTH DEBUG] acquireToken failed:', authError);

          // Check if it's a redirect in progress
          if (
            authError instanceof Error &&
            authError.message.includes('Redirecting')
          ) {
            // Return empty headers to prevent API call during redirect
            return {};
          }

          // Handle token errors in E2E mode - clear and reload
          const authMode = getAuthMode();
          if (
            authError instanceof Error &&
            authError.message.includes('E2E mode active but no token found') &&
            authMode === 'e2e'
          ) {
            // Prevent multiple simultaneous reloads
            if (!isReloadingForAuth) {
              console.log(
                '[AUTH] No token available in E2E mode - clearing and reloading',
              );
              isReloadingForAuth = true;
              clearLocalToken();
              window.location.reload();
            }
            // Return empty headers to prevent further execution
            return {};
          }

          // Increment failure counter for circuit breaker
          incrementAuthFailure();

          // Re-throw to let TanStack Query handle retries/error UI
          throw new Error(
            `Authentication failed: ${authError instanceof Error ? authError.message : 'Unknown error'}`,
          );
        }
      },
    }),
  ],
});

// Export vanilla client for programmatic queries
export const TRPC_VANILLA_CLIENT = trpcClient;

export const TRPC_CLIENT = createTRPCOptionsProxy<TRPCRouter>({
  client: trpcClient,
  queryClient: QUERY_CLIENT,
});
